/* eslint-disable no-unused-expressions,no-unused-vars */
/* global PLATFORM, G */
import { hidden, hide, show } from 'lib/sequence/component/state/hidden'
import map from 'lib/sequence/model/api/map'
import { asyncPipeSpread, asyncPipeSpreadIf, bulk, getFirstItem, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { empty } from 'lib/util/object'
import { presetApprovalStatus } from 'app/organisation/action/detail'
import { get, reset, set } from 'lib/sequence/component/state/value'
import { settings } from 'app/_shared/session'
import sequenceComponentState from 'lib/sequence/component/state'
import displayStatus from 'app/_shared/action/partial/display/status'
import displayFavoriteButtonsFromModel from 'app/_shared/action/partial/favorite/display'
import hideQuickAction from 'app/_shared/events/appbar/quickActions/hide'
import showQuickAction from 'app/_shared/events/appbar/quickActions/show'

const display = x => (get(x) ? show(x) : hide(x))
const bulkDisplay = bulk(display)
const bulkHide = bulk(hide)
const bulkShow = bulk(show)

const {
  set: setHref,
  unset: clearHref,
} = sequenceComponentState('href')

/**
 * Map various serviceItem fields.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapServiceItemData = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { serial } = model[G.CHILDREN]
  const serialValue = serial[G.CACHE]

  const bulkMap = bulk(map(model))

  if (PLATFORM === 'web') {
    const {
      deviceStatuses,
      deviceHeader,
      deviceInfo,
      installedAtProperty,
      serviceByProperty,
    } = components || {}

    const { name } = find(deviceHeader)

    bulkMap(deviceHeader, deviceStatuses, deviceInfo, installedAtProperty, serviceByProperty)

    settings.swapDeviceSerialAndName && set(name, serialValue)
  }

  if (PLATFORM === 'mobile') {
    const { header } = components
    const {
      info,
      serialContainer,
      numberCommissionContainer,
      statusContainer,
      cards,
    } = find(header)

    bulkMap(info, serialContainer, numberCommissionContainer, statusContainer, cards)
  }

  return [components, ...args]
}

/**
 * Map toBeValidated data
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapToBeValidatedData = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const validationCache = model[G.CHILDREN].toBeValidated?.[G.CACHE]
  const { reason = null } = validationCache || {}
  const { header, validation, toBeValidated } = components || {}

  // mapping data
  map(model)(header)

  // conditionally hiding toBeValidated data + checkbox if model has no data
  if (empty(reason)) hide(toBeValidated)
  if (empty(validationCache)) hide(validation)

  return [components, ...args]
}

/**
 * Set the equipment options if available and show/hide the button accordingly.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, ...[*]): Promise<*[]>}
 */
const presetEquipmentOpts = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { btnEquipmentOpts, equipmentOptsDivider } = components || {}

  const hideSection = PLATFORM === 'web'
    ? () => hide(btnEquipmentOpts)
    : () => bulkHide(equipmentOptsDivider, btnEquipmentOpts)

  const showSection = PLATFORM === 'web'
    ? () => show(btnEquipmentOpts)
    : () => bulkShow(equipmentOptsDivider, btnEquipmentOpts)

  if (!settings.suppressEquipmentOptions) {
    const equipmentData = model[G.CHILDREN].equipment[G.CACHE]

    const [
      { value: { custom: { options: equipmentOpts = [] } = {} } = {} },
    ] = equipmentData || [{}]

    equipmentOpts?.length
      ? setKey(equipmentOpts, 'equipmentOptions', module[G.STATE]) && showSection()
      : hideSection()
  } else {
    hideSection()
  }

  return [components, model, ...args]
}

/**
 * Displays the registration box if it's a synthetic service item.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, ...[*]): Promise<*[]>}
 */
const displayRegistrationAlert = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { _rev = '' } = model?.[G.CACHE] || {}
  const isEquipment = _rev.split('-')?.[0] === '0'

  const { notRegistered, buttons } = components

  isEquipment
    ? show(notRegistered)
    : hide(notRegistered)

  if (PLATFORM === 'web') {
    isEquipment
      ? hide(buttons)
      : show(buttons)
  }

  if (PLATFORM === 'mobile') {
    const { quickActions = [] } = module[G.STATE][G.ACTION][G.UI]
    quickActions.forEach((action) => {
      const fn = isEquipment ? hideQuickAction : showQuickAction
      fn(action.key, module, module[G.STATE][G.ACTION][G.COMPONENT], null)
    })
  }

  return [components, ...args]
}
/**
 * Fetch and set the equipment documentation according to data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetEquipmentDoc = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { btnEquipmentDoc, noEquipment } = components || {}
  const { version } = model[G.PROPS]
  const httpAdapter = model[G.ADAPTER][G.HTTP]

  const equipmentData = model[G.CHILDREN].equipment[G.CACHE]
  const [{ key: equipmentKey }] = equipmentData || [{}]

  if (PLATFORM === 'web') {
    equipmentData || settings.suppressEquipmentDevices
      ? hide(noEquipment)
      : show(noEquipment)
  }

  if (!settings.suppressProductDocumentation) {
    const { api: { type: equipmentApi } } = btnEquipmentDoc[G.PROPS]
    const url = equipmentKey ? `/api/v${version}/docs/${equipmentApi}/${equipmentKey}` : null

    // Checking if we fetched the tree before
    const actionState = module[G.ACTIONS]?.equipmentDocumentation?.[G.STATE]
    const existingTree = actionState?.tree ? { value: [{ value: actionState.tree }] } : null

    // const options = { middleware: ({ error, ...rest }) => Object.values(rest) }

    try {
      // Reusing fetched tree or fetching it
      const { value: equipmentDoc } = existingTree
        || (equipmentKey ? await httpAdapter.get({ url }) : {})

      // Saving tree to action state
      const { value: tree } = getFirstItem(equipmentDoc) || {}
      tree && tree?.$children?.length
        ? setKey(tree, 'tree', actionState) && show(btnEquipmentDoc)
        : setKey({ $children: [] }, 'tree', actionState) && hide(btnEquipmentDoc)
    } catch (e) {
      hide(btnEquipmentDoc)
    }
  } else {
    hide(btnEquipmentDoc)
  }

  return [components, ...args]
}

/**
 * Fetch and set the product documentation according to data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, ...[*]): Promise<*[]>}
 */
const presetProductDoc = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { btnProductDoc, noProduct, productDocDivider } = components || {}

  const hideSection = PLATFORM === 'web'
    ? () => hide(btnProductDoc)
    : () => bulkHide(productDocDivider, btnProductDoc)

  const showSection = PLATFORM === 'web'
    ? () => show(btnProductDoc)
    : () => bulkShow(productDocDivider, btnProductDoc)

  const { version } = model[G.PROPS]
  const httpAdapter = model[G.ADAPTER][G.HTTP]

  const equipmentData = model[G.CHILDREN].equipment[G.CACHE]
  const [{ key: equipmentKey }] = equipmentData || [{}]

  const productData = model[G.CHILDREN].product[G.CACHE]
  const { api: { type: productApi } } = btnProductDoc[G.PROPS]
  const [{ key: productKey }] = productData || [{}]

  if (PLATFORM === 'web') {
    productData || settings.suppressProductDevices ? hide(noProduct) : show(noProduct)
  }

  if (!settings.suppressProductDocumentation) {
    const url = productKey ? `/api/v${version}/docs/${productApi}/${productKey}` : null

    // Checking if we fetched the tree before
    const actionState = module[G.ACTIONS]?.productDocumentation?.[G.STATE]
    const existingTree = actionState?.tree ? { value: actionState.tree } : null

    // Reusing fetched tree or fetching it
    const { value: productDoc } = existingTree
      || (equipmentKey && productKey ? await httpAdapter.get({ url }) : {})

    // Saving tree to action state
    const tree = (productDoc || []).map(t => ({ value: t.value }))
    tree && tree.length
      ? setKey(tree, 'tree', actionState) && showSection()
      : setKey([], 'tree', actionState) && hideSection()
  } else {
    hideSection()
  }

  return [components, ...args]
}

/**
 * Shows the `partStatus` section depending on whether the setting is enabled and
 * the equipment has `partsAvailable` set to `false`.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, *, ...[*]): Promise<*[]>}
 */
const displayEquipmentPartsAvailable = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { equipment } = model[G.CHILDREN]
  const { partsAvailable } = getFirstItem(equipment[G.CACHE])?.value || {}

  const { partStatus } = components || {}

  partsAvailable === false
    && settings.showEquipmentPartsAvailable
    && show(partStatus)

  return [components, ...args]
}

/**
 * Fetch and set the product image.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, *, ...[*]): Promise<*[]>}
 */
const presetProductImage = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { product } = model[G.CHILDREN]
  const { avatar } = product[G.CHILDREN]
  const attachment = getFirstItem(avatar[G.CACHE])

  const { product: productImage } = components || {}

  if (PLATFORM === 'mobile' && !attachment) {
    hide(productImage)
  }

  if (attachment) {
    productImage[G.PROPS].attachment = attachment
  }

  return [components, ...args]
}

/**
 * Show or hide the serviceBy section according to available data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const initServiceBy = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { serviceByProperty } = components || {}
  const serviceByData = model[G.CHILDREN].serviceBy[G.CACHE]

  serviceByData ? show(serviceByProperty) : hide(serviceByProperty)

  return [components, ...args]
}

/**
 * Displays the device's details coming from its serviceBy ref.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayContactDetails = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { more, contactDetails, summary1, summary2, servicePhone } = components
  const { serviceBy } = model[G.CHILDREN]

  const { publishServiceContactData, geoMapsLink, service } = serviceBy[G.CACHE]?.[0]?.value || {}

  if (publishServiceContactData) {
    show(more)
    map(serviceBy)(contactDetails)
    setHref(summary1, geoMapsLink)
    setHref(summary2, geoMapsLink)
    set(servicePhone, service?.phone ?? '')
  } else {
    hide(more)
    clearHref(summary1)
    clearHref(summary2)
    reset(servicePhone)
  }

  return [components, ...args]
}

/**
 * Hides several properties in the property box if they have no value.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayAdditionalInformation = module => async (components, ...args) => {
  const { locationExtension, placement, comment } = components || {}

  bulkDisplay(locationExtension, placement, comment)

  return [components, ...args]
}

/**
 * Hides several properties in the property box if they have no value.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetSerial = module => async (components, ...args) => {
  const { serial } = components || {}

  set(serial, ` • ${get(serial)}`)

  return [components, ...args]
}

/**
 * Hides several properties in the property box if they have no value.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetCommissionNumberMobile = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { numberCommission } = model[G.CACHE] || {}
  const { numberCommission: numberCommissionLabel, numberCommissionContainer } = components || {}

  numberCommission || numberCommission === 0
    ? set(numberCommissionLabel, ` • ${numberCommission}`)
    && show(numberCommissionContainer)
    : reset(numberCommissionLabel)
    && hide(numberCommissionContainer)

  return [components, ...args]
}

/**
 * Shows the commission number if found in model data. Otherwise, it hides the element.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): *[]}
 */
const presetCommissionNumberWeb = module => (components, ...args) => {
  const model = module[G.MODEL]
  const { numberCommission } = model[G.CACHE]?.value || {}
  const { numberCommission: numberCommissionLabel } = components

  numberCommission || numberCommission === 0
    ? set(numberCommissionLabel, numberCommission)
      && show(numberCommissionLabel)
    : reset(numberCommissionLabel)
      && hide(numberCommissionLabel)

  return [components, ...args]
}

/**
 * Presets the action title according to the device name and serial
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetTitle = module => async (components, ...args) => {
  const actionUi = module[G.STATE][G.ACTION][G.UI]
  const { value: { name = null, serial = null } = {} } = module[G.MODEL][G.CACHE] || {}

  if (name) {
    actionUi.title = name
    actionUi.suppressTranslation = true
  }

  if (serial) {
    actionUi.subTitle = serial
    actionUi.titleAlign = 'left'
  }

  return [components, ...args]
}

/**
 * Presets the device's location
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetLocation = module => async (components, ...args) => {
  const { [G.INTL]: intl } = module[G.ADAPTER]
  const { installedAt } = module[G.MODEL][G.CHILDREN]
  const installedAtCache = getFirstItem(installedAt[G.CACHE])
  const { address = null, name = null } = installedAtCache?.value || {}

  const { installedAtContainer, installedAt: installedAtField } = components

  if (installedAtCache) {
    name && setKey(name, 'label', installedAtField[G.STATE])
    address && set(installedAtField, intl.address(address, { inline: true }))
  } else {
    hide(installedAtContainer)
  }

  return [components, ...args]
}

/**
 * ServiceItem Detail Action
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  displayFavoriteButtonsFromModel(module),
  mapServiceItemData(module),
  presetProductImage(module),
  presetEquipmentDoc(module),
  presetProductDoc(module),
  presetEquipmentOpts(module),
  displayRegistrationAlert(module),
  asyncPipeSpreadIf(() => PLATFORM === 'web')(
    // mapServiceItemData(module),
    presetCommissionNumberWeb(module),
    mapToBeValidatedData(module),
    displayEquipmentPartsAvailable(module),
    // presetProductImage(module),
    // TODO: The next call makes serviceItem depend on organisation. Move its definition to shared.
    presetApprovalStatus(module)(component)('installedAt'),
    initServiceBy(module),
    displayContactDetails(module),
    displayAdditionalInformation(module),
  ),
  asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
    presetSerial(module),
    presetCommissionNumberMobile(module),
    presetTitle(module),
    presetLocation(module),
  ),
  displayStatus({ component: 'status', parent: 'statusContainer' })(module),
)(find(component), ...args)
